import React from 'react'
import 'react-toastify/dist/ReactToastify.css'
import {Outlet, useLocation, Link} from 'react-router-dom'
import {styled} from '@mui/material/styles'
import {Box, Typography} from '@mui/material'
import Footer from './Footer'

// ─── Theme tokens (matching Admin Panel) ────────────────────────
const NAVY_DEEP = '#0a1628'
const NAVY_MID = '#142440'
const NAVY_SOFT = '#1a2d4a'
const TEAL = '#4CCEAC'
const INDIGO = '#3783e7'
const TEXT_DARK = '#0d1b2a'
const TEXT_MUTED = '#6b7280'
const SURFACE_LIGHT = 'rgba(255,255,255,0.04)'
const BORDER_LIGHT = 'rgba(255,255,255,0.08)'

// ─── Styled components ─────────────────────────────────────────

const PageWrapper = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  overflow: 'hidden',
  fontFamily: 'Poppins, sans-serif',
})

const LeftPanel = styled(Box)(({theme}) => ({
  flex: '0 0 50%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '40px 48px',
  background: `linear-gradient(140deg, ${NAVY_DEEP} 0%, ${NAVY_MID} 55%, ${NAVY_SOFT} 100%)`,
  color: '#fff',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 520,
    height: 520,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(76,206,172,0.10) 0%, transparent 70%)',
    bottom: -180,
    left: -120,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: 320,
    height: 320,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${INDIGO}12 0%, transparent 70%)`,
    top: -100,
    right: -80,
    pointerEvents: 'none',
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}))

const PillRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  position: 'relative',
  zIndex: 1,
})

const Pill = styled('span')({
  padding: '4px 10px',
  border: `1px solid ${BORDER_LIGHT}`,
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 500,
  color: 'rgba(255,255,255,0.7)',
  background: SURFACE_LIGHT,
})

const RightPanel = styled(Box)(({theme}) => ({
  flex: '0 0 50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#fff',
  padding: '40px 24px',
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 100%',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '32px 20px',
    alignItems: 'flex-start',
    paddingTop: 56,
  },
}))

const FormContainer = styled(Box)({
  width: '100%',
  maxWidth: 480,
})

// ─── Data ───────────────────────────────────────────────────────

const HERO_STATS = [
  {value: '20+', label: 'Courier Partners'},
  {value: '24,000+', label: 'PIN Codes Serviced'},
  {value: '500+', label: 'Appointment Cities'},
]

const FEATURES = [
  {icon: '⚡', label: 'Sub 4-Hour Delivery'},
  {icon: '🏎️', label: 'Same & Next-Day Delivery'},
  {icon: '🚛', label: 'PTL & Appointments'},
  {icon: '🎯', label: 'PACE — AI Courier Allocation'},
  {icon: '👁️', label: 'NETRA — RTO Prediction'},
  {icon: '📊', label: 'Real-Time Insights'},
]

const PILLS = ['Pan-India Coverage', 'D2C Brands', 'B2B & PTL', 'Marketplace Ready']

// ─── Component ──────────────────────────────────────────────────

export default function AuthLayout() {
  const location = useLocation()
  const isSignup = location.pathname.includes('signup')

  return (
    <PageWrapper>
      {/* ── Left branding panel ── */}
      <LeftPanel>
        <Box sx={{mb: 5}}>
          <img src='/img/prozo-logo3.svg' alt='Prozo' style={{height: 25, opacity: 0.9}} />
        </Box>

        <Box sx={{position: 'relative', zIndex: 1}}>

          <Typography sx={{fontSize: {md: 24, lg: 30}, fontWeight: 700, lineHeight: 1.25, mb: 1.5, color: '#fff'}}>
            Speed-First{' '}
            <Box component='span' sx={{color: TEAL}}>Omni-Channel</Box>{' '}
            Shipping Platform
          </Typography>

          <Typography sx={{fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, mb: 5, maxWidth: 420}}>
            Every demand channel, every delivery mode — one high-integrity platform built for brands of tomorrow.
          </Typography>

          {/* ── Hero stats ── */}
          <Box sx={{display: 'flex', gap: 4, mb: 5}}>
            {HERO_STATS.map((s, i) => (
              <Box key={s.label} sx={{display: 'flex', alignItems: 'center', gap: 4}}>
                <Box>
                  <Typography sx={{fontSize: 26, fontWeight: 700, color: TEAL, lineHeight: 1}}>{s.value}</Typography>
                  <Typography sx={{fontSize: 11.5, color: 'rgba(255,255,255,0.5)', mt: 0.5}}>{s.label}</Typography>
                </Box>
                {i < HERO_STATS.length - 1 && <Box sx={{width: '1px', height: 32, background: BORDER_LIGHT}} />}
              </Box>
            ))}
          </Box>

          {/* ── Feature highlights ── */}
          <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 2, mb: 5}}>
            {FEATURES.map((f) => (
              <Box key={f.label} sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
                <Typography sx={{fontSize: 18, lineHeight: 1}}>{f.icon}</Typography>
                <Typography sx={{fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 500}}>{f.label}</Typography>
              </Box>
            ))}
          </Box>

          <PillRow>
            {PILLS.map((p) => <Pill key={p}>{p}</Pill>)}
          </PillRow>
        </Box>
      </LeftPanel>

      {/* ── Right panel — 50% login / 25% actions / 25% legal ── */}
      <RightPanel>
        <Box sx={{width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', height: '100%'}}>

          {/* ═══ Top 50%: Login ═══ */}
          <Box sx={{flex: '2 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 2.5, mb: 1.5}}>
            {/* Branded header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5,
              p: '12px 16px', borderRadius: '10px',
              background: `linear-gradient(135deg, ${NAVY_DEEP} 0%, ${NAVY_MID} 100%)`,
            }}>
              <img src='/img/prozo-logo3.svg' alt='Prozo' style={{height: 15, opacity: 0.95}} />
              <Box>
                <Typography sx={{fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: 'Poppins, sans-serif', lineHeight: 1.2}}>
                  Merchant Panel
                </Typography>
                <Typography sx={{fontSize: 9.5, color: 'rgba(255,255,255,0.55)', fontFamily: 'Poppins, sans-serif'}}>
                  {isSignup ? 'Create your account' : 'Sign in to continue'}
                </Typography>
              </Box>
            </Box>

            <Outlet />
          </Box>

          {/* ═══ Middle 25%: Quick Actions ═══ */}
          {!isSignup && (
            <Box sx={{flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 2, mb: 1.5}}>
              <Typography sx={{fontSize: 9.5, fontWeight: 700, color: '#b0b0b0', fontFamily: 'Poppins, sans-serif', textTransform: 'uppercase', letterSpacing: '0.8px', mb: 1.25}}>
                Quick Actions
              </Typography>
              <Box sx={{display: 'flex', gap: 1.25}}>
                <Box component={Link} to='/signup' sx={{
                  flex: 1, textDecoration: 'none', py: 1.25, px: 1.5, borderRadius: '10px',
                  bgcolor: NAVY_DEEP, color: '#fff',
                  display: 'flex', alignItems: 'center', gap: 1,
                  transition: 'all 0.2s',
                  '&:hover': {bgcolor: NAVY_MID, boxShadow: '0 4px 16px rgba(10,22,40,0.2)', transform: 'translateY(-1px)'},
                }}>
                  <Typography sx={{fontSize: 16, lineHeight: 1}}>🚀</Typography>
                  <Box>
                    <Typography sx={{fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins, sans-serif', lineHeight: 1.2}}>Get Started Free</Typography>
                    <Typography sx={{fontSize: 8.5, color: 'rgba(255,255,255,0.5)', fontFamily: 'Poppins, sans-serif'}}>Create account</Typography>
                  </Box>
                </Box>
                <Box component='a' href='https://t.proship.in/' target='_blank' rel='noopener noreferrer' sx={{
                  flex: 1, textDecoration: 'none', py: 1.25, px: 1.5, borderRadius: '10px',
                  border: '1.5px solid #e5e7eb',
                  display: 'flex', alignItems: 'center', gap: 1,
                  transition: 'all 0.2s',
                  '&:hover': {borderColor: INDIGO, boxShadow: `0 4px 16px ${INDIGO}10`, transform: 'translateY(-1px)'},
                }}>
                  <Typography sx={{fontSize: 16, lineHeight: 1}}>📦</Typography>
                  <Box>
                    <Typography sx={{fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins, sans-serif', color: '#0d1b2a', lineHeight: 1.2}}>Track Package</Typography>
                    <Typography sx={{fontSize: 8.5, color: '#9ca3af', fontFamily: 'Poppins, sans-serif'}}>Enter AWB</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* ═══ Bottom 25%: Legal & Contact ═══ */}
          <Box sx={{flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', p: 2}}>
            <Footer />
          </Box>
        </Box>
      </RightPanel>
    </PageWrapper>
  )
}
